/**
 * ERDiagram — renders a Mermaid ER diagram for the detected relationships.
 *
 * The backend's relationship_detector already produces a valid Mermaid
 * `erDiagram` string; we just need to render it client-side. Includes a
 * plaintext fallback when mermaid parse fails so the user always sees
 * something useful.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Maximize2, Minus, Plus, RotateCcw } from "lucide-react";
import type { MermaidConfig } from "mermaid";

import { renderMermaid } from "../../lib/mermaidRender";

// This diagram's mermaid config, applied on each render (lib/mermaidRender.ts).
// `strict` security keeps user-supplied content from injecting scripts; the
// theme blends with the dark UI.
const ER_MERMAID_CONFIG: MermaidConfig = {
  securityLevel: "strict",
  theme: "dark",
  themeVariables: {
    darkMode: true,
    background: "transparent",
    primaryColor: "#1a2233",
    primaryTextColor: "#e5e7eb",
    primaryBorderColor: "#34d399",
    lineColor: "#64748b",
    textColor: "#cbd5e1",
    fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif",
    fontSize: "16px",
  },
  // useMaxWidth:false is load-bearing, not cosmetic: Mermaid otherwise sets
  // the root <svg> to width:100% while its viewBox keeps the diagram's true
  // (large) intrinsic size, so the browser scales EVERYTHING — including
  // this fontSize — down by containerWidth/intrinsicWidth before our own
  // zoom control ever runs. For a 100+-table diagram that shrink factor can
  // be 20-50x, which the 1000% zoom ceiling can't undo. Disabling it keeps
  // text at its true 16px (12pt) and lets computeFitZoom() below do the
  // (bounded, undoable) scaling instead.
  er: { fontSize: 16, useMaxWidth: false },
};

interface ERDiagramProps {
  mermaid?: string | null;
  /**
   * Optional relationships list — used to build a minimal Mermaid ER
   * definition when the backend didn't supply `relationship_mermaid`.
   */
  relationships?: ERRelationship[];
}

export interface ERRelationship {
  source_table: string;
  target_table: string;
  source_column?: string;
  target_column?: string;
  // The profiler sends FK candidates, which name their columns as lists.
  source_columns?: string[];
  target_columns?: string[];
  estimated_cardinality?: string;
  cardinality?: string;
}

/** The column(s) one end of a relationship joins on, whichever shape it arrived in. */
export function relationshipColumns(
  r: ERRelationship,
  side: "source" | "target",
): string {
  const single = side === "source" ? r.source_column : r.target_column;
  const list = side === "source" ? r.source_columns : r.target_columns;
  return single || (list ?? []).join(", ");
}

/**
 * Convert a relationships[] array into a Mermaid erDiagram string.
 * Cardinalities are approximated with Mermaid's ER notation:
 *   1:1 → ||--||
 *   1:N → ||--o{
 *   N:1 → }o--||
 *   N:N → }o--o{
 * A relationship without a cardinality is a foreign key (source holds the
 * reference, target is the parent), so it defaults to N:1.
 */
/** A table as a quoted mermaid ER entity name (`"` is not allowed inside). */
function entityName(table: string | undefined): string {
  const clean = (table ?? "").replace(/"/g, "").trim();
  return clean ? `"${clean}"` : "";
}

export function buildMermaidFromRelationships(
  rels: NonNullable<ERDiagramProps["relationships"]>,
): string {
  if (!rels || rels.length === 0) return "";
  // Left to right: the panel is wide and short, and the default top-to-bottom
  // layout stacked four tables into a 2,600px column that fit at 25% zoom.
  const lines: string[] = ["erDiagram", "    direction LR"];
  const seen = new Set<string>();
  for (const r of rels) {
    // Quoted: a table named like an ER keyword (class, one, to, ...) or with
    // punctuation would otherwise fail to parse and lose the whole diagram.
    const src = entityName(r.source_table);
    const tgt = entityName(r.target_table);
    if (!src || !tgt) continue;
    const key = `${src}→${tgt}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const card = (
      r.estimated_cardinality ||
      r.cardinality ||
      "N:1"
    ).toUpperCase();
    const connector =
      card === "1:1"
        ? "||--||"
        : card === "N:1"
          ? "}o--||"
          : card === "N:N" || card === "M:M" || card === "M:N"
            ? "}o--o{"
            : "||--o{";
    const label = (relationshipColumns(r, "source") || "ref").replace(/"/g, "");
    lines.push(`    ${src} ${connector} ${tgt} : "${label}"`);
  }
  return lines.length > 1 ? lines.join("\n") : "";
}

/**
 * Shrink the SVG's canvas to what is actually drawn. Mermaid's ER renderer
 * can report a canvas far larger than its content (a 1302x247 diagram of the
 * OCC demo's four tables came back 1318x2231), and fitting that empty canvas
 * to the panel rendered the diagram at about a quarter of the size it needed.
 * Idempotent; leaves the SVG alone when it cannot be measured yet.
 */
function trimToContent(svgEl: SVGSVGElement) {
  let box: DOMRect | undefined;
  try {
    box = svgEl.getBBox?.();
  } catch {
    return;
  }
  if (!box || box.width <= 0 || box.height <= 0) return;
  const pad = 8;
  const w = box.width + 2 * pad;
  const h = box.height + 2 * pad;
  const vb = svgEl.viewBox?.baseVal;
  if (vb && Math.abs(vb.width - w) < 1 && Math.abs(vb.height - h) < 1) return;
  svgEl.setAttribute("viewBox", `${box.x - pad} ${box.y - pad} ${w} ${h}`);
  svgEl.setAttribute("width", String(w));
  svgEl.setAttribute("height", String(h));
}

export function ERDiagram({ mermaid: source, relationships }: ERDiagramProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [err, setErr] = useState<string | null>(null);
  const [svg, setSvg] = useState<string | null>(null);

  const definition =
    (source && source.trim()) ||
    buildMermaidFromRelationships(relationships || []);

  useEffect(() => {
    let cancelled = false;
    if (!definition) {
      setSvg(null);
      setErr(null);
      return;
    }
    const id = `er-${Math.random().toString(36).slice(2, 9)}`;
    (async () => {
      try {
        const { svg: rendered } = await renderMermaid(
          id,
          definition,
          ER_MERMAID_CONFIG,
        );
        if (!cancelled) {
          setSvg(rendered);
          setErr(null);
        }
      } catch (e) {
        if (!cancelled) {
          setErr(e instanceof Error ? e.message : String(e));
          setSvg(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [definition]);

  if (!definition) {
    return (
      <p className="text-sm text-white/55">
        No relationships detected — there's nothing to diagram for this source.
      </p>
    );
  }

  if (err) {
    return (
      <div className="glass-surface rounded-xl p-4 space-y-2">
        <p className="text-xs text-red-300">
          Couldn't render ER diagram: {err}
        </p>
        {/* Fall back to the raw Mermaid source so users aren't empty-handed */}
        <pre className="text-[11px] text-white/55 font-mono whitespace-pre-wrap overflow-auto max-h-64">
          {definition}
        </pre>
      </div>
    );
  }

  return <ERDiagramViewer containerRef={containerRef} svg={svg} />;
}

/**
 * ERDiagramViewer — wraps the rendered SVG with zoom + pan controls.
 *
 * Users kept asking to read small relationships in larger schemas; the
 * browser's native CSS zoom isn't discoverable and doesn't survive a
 * rerender. Mouse-wheel zoom + click-and-drag pan + explicit +/-/reset
 * buttons give predictable control without external libraries.
 */
function ERDiagramViewer({
  containerRef,
  svg,
}: {
  containerRef: React.RefObject<HTMLDivElement | null>;
  svg: string | null;
}) {
  // `null` = compute-on-render (auto-fit). A concrete number = user has
  // taken control via zoom-in/out/wheel/reset.
  const [zoom, setZoom] = useState<number | null>(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragOrigin = useRef<{
    x: number;
    y: number;
    ox: number;
    oy: number;
  } | null>(null);

  const clamp = (v: number) => Math.min(10, Math.max(0.1, v));

  /**
   * Compute the zoom level that fits the rendered SVG inside the scroll
   * container with a small margin. Called after mount + whenever the svg
   * HTML changes. If the measurements aren't ready yet (ResizeObserver
   * race on first paint), falls back to 1.
   */
  const computeFitZoom = useCallback((): number => {
    const host = containerRef.current;
    if (!host) return 1;
    const hostRect = host.getBoundingClientRect();
    const svgEl = host.querySelector("svg") as SVGSVGElement | null;
    if (!svgEl) return 1;
    trimToContent(svgEl);
    let svgW = 0;
    let svgH = 0;
    // viewBox is the most reliable intrinsic size Mermaid produces.
    const vb = svgEl.viewBox?.baseVal;
    if (vb && vb.width > 0 && vb.height > 0) {
      svgW = vb.width;
      svgH = vb.height;
    } else {
      const b = svgEl.getBBox?.();
      if (b) {
        svgW = b.width;
        svgH = b.height;
      } else {
        svgW = svgEl.clientWidth || 1;
        svgH = svgEl.clientHeight || 1;
      }
    }
    if (svgW <= 0 || svgH <= 0 || hostRect.width <= 0 || hostRect.height <= 0) {
      return 1;
    }
    // Fit the SVG's true intrinsic size (now that mermaid's own
    // useMaxWidth-driven auto-shrink is disabled in ensureMermaid()) inside
    // the panel with a small margin, so text renders at real, undoable
    // scale. A small diagram (few tables) can end up over 100% here — cap at
    // 2.5x so a 2-table schema doesn't zoom in absurdly far by default; the
    // user can still zoom further via the +/scroll controls.
    const margin = 0.92;
    const fit = Math.min(
      (hostRect.width * margin) / svgW,
      (hostRect.height * margin) / svgH,
    );
    return clamp(Math.min(fit, 2.5));
  }, [containerRef]);

  // Auto-fit on mount / whenever the rendered SVG changes. Users can
  // still override via zoom controls; resetting restores auto-fit.
  //
  // Why double-RAF + timeout: mermaid injects the SVG via
  // dangerouslySetInnerHTML. A single RAF fires before the browser has
  // finished painting the new DOM nodes, so getBoundingClientRect() and
  // viewBox.baseVal both return zero — causing computeFitZoom() to fall
  // back to 1 and the diagram renders huge/unreadable. Two nested RAFs
  // plus a 150 ms safety timeout guarantee the SVG is measured after
  // layout has truly settled.
  useEffect(() => {
    if (!svg) return;
    // Start at 100% immediately (computeFitZoom confirms the real fit a
    // couple frames later); avoids a flash at a wildly wrong zoom now that
    // the SVG renders at its true (potentially large) intrinsic size.
    setZoom(1);
    let raf1: number, raf2: number;
    let timer: ReturnType<typeof setTimeout>;
    const measure = () => {
      const z = computeFitZoom();
      if (z > 0.1) {
        setZoom(z);
      }
    };
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        measure();
        // Safety net: if viewBox wasn't ready yet (e.g. mermaid async
        // post-processing), retry after 150 ms.
        timer = setTimeout(measure, 150);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      clearTimeout(timer);
    };
  }, [svg, computeFitZoom]);

  // Re-fit when the container resizes (sidebar opens/closes, window resize).
  // We track a `userZoomed` flag separately so we don't clobber a zoom
  // the user deliberately set via +/- buttons.
  const userZoomedRef = useRef(false);

  const zoomIn = () => {
    userZoomedRef.current = true;
    setZoom(clamp(effectiveZoom * 1.2));
  };
  const zoomOut = () => {
    userZoomedRef.current = true;
    setZoom(clamp(effectiveZoom / 1.2));
  };
  const reset = () => {
    userZoomedRef.current = false;
    setOffset({ x: 0, y: 0 });
    setZoom(computeFitZoom());
  };

  useEffect(() => {
    const host = containerRef.current;
    if (!host || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => {
      if (userZoomedRef.current) return; // user is in control — don't clobber
      const z = computeFitZoom();
      if (z > 0.1) setZoom(z);
    });
    ro.observe(host);
    return () => ro.disconnect();
  }, [containerRef, computeFitZoom]);

  const effectiveZoom = zoom ?? 1;

  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    userZoomedRef.current = true;
    setZoom(clamp(effectiveZoom * (e.deltaY < 0 ? 1.1 : 1 / 1.1)));
  };
  const onMouseDown = (e: React.MouseEvent) => {
    setDragging(true);
    dragOrigin.current = {
      x: e.clientX,
      y: e.clientY,
      ox: offset.x,
      oy: offset.y,
    };
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragging || !dragOrigin.current) return;
    const { x, y, ox, oy } = dragOrigin.current;
    setOffset({ x: ox + (e.clientX - x), y: oy + (e.clientY - y) });
  };
  const onMouseUp = () => {
    setDragging(false);
    dragOrigin.current = null;
  };

  return (
    <div className="glass-surface rounded-xl overflow-hidden">
      {/* Zoom toolbar */}
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-white/5 bg-white/[0.02]">
        <button
          onClick={zoomOut}
          className="p-1 rounded-md text-white/60 hover:text-white hover:bg-white/10"
          title="Zoom out"
          aria-label="Zoom out"
        >
          <Minus className="w-3.5 h-3.5" />
        </button>
        <span
          className="text-[11px] font-mono text-white/55 w-12 text-center tabular-nums"
          title={zoom === null ? "Auto-fit" : "Current zoom"}
        >
          {Math.round(effectiveZoom * 100)}%
        </span>
        <button
          onClick={zoomIn}
          className="p-1 rounded-md text-white/60 hover:text-white hover:bg-white/10"
          title="Zoom in"
          aria-label="Zoom in"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={reset}
          className="p-1 rounded-md text-white/60 hover:text-white hover:bg-white/10"
          title="Reset zoom + pan"
          aria-label="Reset view"
        >
          <RotateCcw className="w-3.5 h-3.5" />
        </button>
        <span className="ml-auto text-[11px] text-white/40">
          scroll to zoom · drag to pan
        </span>
      </div>
      <div
        ref={containerRef}
        className="overflow-auto relative"
        style={{
          cursor: dragging ? "grabbing" : "grab",
          height: 700,
          touchAction: "none",
        }}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        role="img"
        aria-label="Entity-relationship diagram — scroll wheel to zoom, drag to pan"
      >
        <div
          style={{
            // Anchor top-left (not centered) so a large diagram starts at its
            // top-left corner and scrolls naturally, instead of overflowing
            // from the centre as an unreadable sliver.
            position: "absolute",
            left: 16,
            top: 16,
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${effectiveZoom})`,
            transformOrigin: "top left",
            transition: dragging ? "none" : "transform 120ms ease-out",
            userSelect: "none",
          }}
          dangerouslySetInnerHTML={svg ? { __html: svg } : undefined}
        />
      </div>
      <div className="flex items-center justify-end px-2 py-1 border-t border-white/5 bg-white/[0.02]">
        <button
          onClick={() => {
            setOffset({ x: 0, y: 0 });
            setZoom(computeFitZoom());
          }}
          className="text-[11px] text-white/50 hover:text-white/80 px-2 py-0.5 rounded-md hover:bg-white/10 flex items-center gap-1"
          title="Auto-fit the diagram to the panel"
        >
          <Maximize2 className="w-3 h-3" />
          Fit
        </button>
      </div>
    </div>
  );
}
